"use client";
import Link from "next/link";
import { Trees, Mail, Phone, MapPin, Instagram, Facebook } from "lucide-react";
import { useAppSelector } from "@/hooks/hooks";
import { ISettings } from "@/store/home/homeModel";
import { IActivity } from "@/store/activities/activityModal";

export function Footer() {
  const SettingList = useAppSelector((state) => state.home.SettingList);
  const settings: Record<string, ISettings> = SettingList?.reduce(
    (acc, item) => {
      acc[item.SettingKey] = item;
      return acc;
    },
    {} as Record<string, ISettings>,
  );
  const ActivityList = useAppSelector((state) => state.activity.ActivityList);

  return (
    <footer className="bg-gradient-to-br from-[#1F3D2B] to-[#2A4F38] text-white py-20 min-h-[450px]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <Trees className="w-10 h-10 text-white" />
              <h2
                className="text-2xl"
                style={{ fontFamily: "var(--font-playfair), serif" }}
              >
                ARANYA
              </h2>
            </div>
            <p className="text-white/90 mb-6 leading-relaxed">
              A nature retreat where wilderness meets comfort. Experience the
              untamed beauty of forests near Gir National Park.
            </p>
            <div className="flex gap-4">
              {settings?.instagram?.SettingValue && (
                <a
                  href={settings?.instagram?.SettingValue}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow us on Instagram"
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#E07A2F] transition-colors flex items-center justify-center"
                >
                  <Instagram className="w-5 h-5" />
                </a>
              )}
              {settings?.facebook?.SettingValue && (
                <a
                  href={settings?.facebook?.SettingValue}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow us on Facebook"
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#E07A2F] transition-colors flex items-center justify-center"
                >
                  <Facebook className="w-5 h-5" />
                </a>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3
              className="text-xl mb-6"
              style={{ fontFamily: "var(--font-playfair), serif" }}
            >
              Quick Links
            </h3>
            <ul className="space-y-3">
              <li>
                <Link
                  href="/"
                  className="text-white/85 hover:text-[#E07A2F] transition-colors"
                >
                  Home
                </Link>
              </li>
              <li>
                <Link
                  href="/about-us/"
                  className="text-white/85 hover:text-[#E07A2F] transition-colors"
                >
                  About Us
                </Link>
              </li>
              <li>
                <Link
                  href="/rooms/"
                  className="text-white/85 hover:text-[#E07A2F] transition-colors"
                >
                  Rooms
                </Link>
              </li>
              <li>
                <Link
                  href="/activities/"
                  className="text-white/85 hover:text-[#E07A2F] transition-colors"
                >
                  Activities
                </Link>
              </li>
              <li>
                <Link
                  href="/gallery/"
                  className="text-white/85 hover:text-[#E07A2F] transition-colors"
                >
                  Gallery
                </Link>
              </li>
              <li>
                <Link
                  href="/blogs/"
                  className="text-white/85 hover:text-[#E07A2F] transition-colors"
                >
                  Blogs
                </Link>
              </li>
              <li>
                <Link
                  href="/contact-us/"
                  className="text-white/85 hover:text-[#E07A2F] transition-colors"
                >
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Experiences */}
          <div>
            <h3
              className="text-xl mb-6"
              style={{ fontFamily: "var(--font-playfair), serif" }}
            >
              Experiences
            </h3>
            <ul className="space-y-3">
              {ActivityList.map((exp: IActivity) => (
                <li key={exp.ID}>
                  <Link
                    href={`/activities/${exp.ActivityURL}/`}
                    className="text-white/85 hover:text-[#E07A2F] transition-colors"
                  >
                    {exp.ActivityName}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3
              className="text-xl mb-6"
              style={{ fontFamily: "var(--font-playfair), serif" }}
            >
              Get In Touch
            </h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#E07A2F] mt-1 flex-shrink-0" />
                <a
                  href={settings?.addressmap?.SettingValue || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`text-white/85 ${settings?.addressmap?.SettingValue ? "hover:text-[#E07A2F] transition-colors" : ""}`}
                >
                  {settings?.address?.SettingValue ||
                    "Near Gir National Park, Sasan Gir, Gujarat"}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-[#E07A2F] flex-shrink-0" />
                <a
                  href={`tel:${settings?.PhoneNo?.SettingValue || "+91 9876543210"}`}
                  className={`text-white/85 ${settings?.PhoneNo?.SettingValue ? "hover:text-[#E07A2F] transition-colors" : ""}`}
                >
                  {settings?.PhoneNo?.SettingValue || "+91 9876543210"}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-[#E07A2F] flex-shrink-0" />
                <a
                  href={`mailto:${settings?.email?.SettingValue || "hello@aranya.com"}`}
                  className={`text-white/85 ${settings?.email?.SettingValue ? "hover:text-[#E07A2F] transition-colors" : ""}`}
                >
                  {settings?.email?.SettingValue || "hello@aranya.com"}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/80 text-center md:text-left">
            © {new Date().getFullYear()} Aranya Nature Retreat. All rights
            reserved.
            <br />
            Developed by{" "}
            <a
              href="https://aseuminfotech.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#E07A2F] hover:text-white transition-colors"
            >
              Aseum Infotech
            </a>
          </p>
          <div className="flex gap-6 text-sm">
            <Link
              href="/privacy-policy/"
              className="text-white/80 hover:text-[#E07A2F] transition-colors"
            >
              Privacy Policy
            </Link>
            <Link
              href="/terms-and-condition/"
              className="text-white/80 hover:text-[#E07A2F] transition-colors"
            >
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
